<template>
  <section class="rms-menu bg-pale-stone" id="menu" aria-labelledby="menu-heading-t2">
    <div class="max-w-[1400px] mx-auto">
      <div class="grid grid-cols-1 md:grid-cols-12 border-x border-charcoal-blue">
        <div class="md:col-span-12 px-4 py-8 sm:px-6 md:py-12 md:px-12 lg:p-20 border-b border-charcoal-blue flex flex-col md:flex-row justify-between items-end gap-8">
          <div>
            <h2 id="menu-heading-t2" class="heading-utilitarian text-6xl md:text-8xl font-extrabold leading-none text-charcoal-blue">{{ sectionTitle || $t('public.ourMenu') }}</h2>
          </div>
          <div class="text-right hidden md:block">
            <p class="font-mono text-[10px] uppercase opacity-40">Ref: MENU_A_REV04</p>
          </div>
        </div>
        <p v-if="!displayGroups.length" class="md:col-span-12 px-4 py-12 text-center text-charcoal-blue/80 font-mono">
          {{ emptyMessage || $t('public.noMenuItems') }}
        </p>
        <div
          v-else
          v-for="(group, gIdx) in displayGroups"
          :key="gIdx"
          class="md:col-span-6 px-4 py-8 sm:px-6 md:p-12 border-b md:border-b-0 border-r border-charcoal-blue bg-white"
          :class="{ 'md:border-r-0': gIdx === displayGroups.length - 1 }"
        >
          <div v-if="group.image_url" class="aspect-square w-full max-w-[140px] md:max-w-[180px] rounded overflow-hidden mb-6">
            <img
              :src="group.image_url"
              :alt="group.category_name || 'Category'"
              loading="lazy"
              class="w-full h-full object-cover"
            />
          </div>
          <h3 class="heading-utilitarian text-4xl font-extrabold mb-12 flex flex-col gap-1 text-charcoal-blue">
            <span class="flex items-center gap-4">
              <span class="bg-charcoal-blue text-white px-3 py-1 text-xl">{{ String(gIdx + 1).padStart(2, '0') }}</span>
              <span :style="categoryUnavailableNow(group.availability) ? { opacity: 0.8 } : undefined">{{ group.category_name }}</span>
              <div class="flex-grow border-t-2 border-dotted border-charcoal-blue/20"></div>
            </span>
            <span v-if="formatAvailabilityForDisplay(group.availability, now)" class="text-sm font-mono font-medium uppercase tracking-wider text-charcoal-blue/70">{{ formatAvailabilityForDisplay(group.availability, now) }}</span>
          </h3>
          <p
            v-if="group.category_description"
            class="font-mono text-sm text-charcoal-blue/80 mb-8 max-w-prose leading-relaxed"
          >
            {{ group.category_description }}
          </p>
          <div
            class="space-y-16 rms-category-items"
            :class="{ 'rms-category-unavailable': categoryUnavailableNow(group.availability) }"
          >
            <div v-for="item in group.items" :key="item.uuid" class="relative">
              <PublicMenuItemOwnerEditFab
                v-if="showOwnerEditFab"
                :item-uuid="item.catalog_item_uuid || item.uuid"
                :visible="showOwnerEditFab"
              />
              <div
                v-if="item.type === 'with_variants'"
                class="rms-menu-item-card rms-menu-item-card--no-click group border-b border-concrete-gray pb-8 last:border-0 w-full text-left"
              >
                <button
                  type="button"
                  data-testid="public-menu-item"
                  class="rms-t2-variant-header w-full text-left border-none bg-transparent p-0 cursor-pointer"
                  :class="{ 'rms-item-unavailable': !isItemClickable(item, group) }"
                  :aria-disabled="!isItemClickable(item, group)"
                  @click="isItemClickable(item, group) ? onVariantHeaderClick(item, group.category_name, $event) : showUnavailableToast(group, item, now)"
                >
                  <div class="flex w-full items-center gap-4">
                    <img
                      v-if="itemCardDisplayImageUrl(item)"
                      :src="itemCardDisplayImageUrl(item)"
                      :alt="item.name || 'Untitled'"
                      loading="lazy"
                      class="w-20 h-20 md:w-24 md:h-24 shrink-0 aspect-square object-cover rounded"
                    />
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center justify-between gap-4 w-full mb-3">
                        <div class="flex flex-wrap items-center gap-2 min-w-0" :class="item.is_available === false ? 'text-charcoal-blue/60' : 'group-hover:[&_.heading-utilitarian]:text-[var(--t2-primary)]'">
                          <span class="heading-utilitarian text-3xl transition-colors" :style="itemUnavailableNow(item.availability) ? { opacity: 0.8 } : undefined">{{ item.name || 'Untitled' }}</span>
                        </div>
                        <span
                          v-if="item.is_available !== false && isVariantSkuPubliclyOrderable(resolvedVariantSku(item)) && resolvedVariantSku(item)?.price != null"
                          class="font-mono font-bold text-xl rms-t2-accent shrink-0"
                          :style="itemUnavailableNow(item.availability) ? { opacity: 0.8 } : undefined"
                        >
                          {{ formatPrice(resolvedVariantSku(item).price) }}
                        </span>
                        <span v-else-if="item.is_available === false" class="font-mono text-sm text-charcoal-blue/60 shrink-0">{{ $t('public.notAvailable') }}</span>
                      </div>
                      <p v-if="formatAvailabilityForDisplay(item.availability, now)" class="text-xs font-mono uppercase tracking-wider text-charcoal-blue/60 mt-0.5">{{ formatAvailabilityForDisplay(item.availability, now) }}</p>
                      <p v-if="item.description" class="text-sm text-charcoal-blue/60 font-medium uppercase tracking-wider">{{ item.description }}</p>
                      <span v-if="itemTags(item).length" class="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <span
                          v-for="tag in itemTags(item)"
                          :key="tag.uuid"
                          class="rms-public-tag inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-xs uppercase font-medium min-h-[28px] transition-[opacity,background-color] duration-200"
                          :style="tagStyle(tag)"
                          :title="tag.text"
                          role="img"
                          :aria-label="tag.text"
                          tabindex="0"
                        >
                          <span class="material-symbols-outlined text-sm shrink-0" style="color: var(--tag-fg)" aria-hidden="true">{{ tag.icon || 'label' }}</span>
                          <span v-if="tag.text" class="whitespace-nowrap" style="color: var(--tag-fg)">{{ tag.text }}</span>
                        </span>
                      </span>
                    </div>
                  </div>
                </button>
                <div
                  v-if="variantSkus(item).length && variantOptionGroups(item).length"
                  class="mt-3 space-y-2 w-full pl-0 sm:pl-[5.5rem] md:pl-[7.5rem]"
                  @click.stop
                >
                  <div
                    v-for="optGroup in variantOptionGroups(item)"
                    :key="`${item.uuid}-${optGroup.name}`"
                    class="space-y-1"
                  >
                    <label
                      class="block text-xs font-mono uppercase tracking-wider text-charcoal-blue/60 font-medium"
                      :for="variantSelectId('rms-t2', item, optGroup.name)"
                    >
                      {{ optGroup.name || $t('public.option') }}
                    </label>
                    <select
                      :id="variantSelectId('rms-t2', item, optGroup.name)"
                      class="rms-t2-variant-select w-full min-h-[44px] rounded border border-charcoal-blue/20 bg-white px-3 py-2 text-sm text-charcoal-blue font-mono focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--t2-primary,#1152d4)] focus-visible:ring-offset-2"
                      :value="variantSelectionValue(item, optGroup.name)"
                      :disabled="!isItemClickable(item, group)"
                      :aria-label="`${optGroup.name || $t('public.option')}: ${item.name || ''}`"
                      @change="onVariantGroupChange(item, optGroup.name, $event.target.value)"
                    >
                      <option
                        v-for="val in (optGroup.values || [])"
                        :key="String(val)"
                        :value="val"
                        :disabled="!isVariantGroupValueOrderable(item, optGroup.name, val)"
                      >
                        {{ val }}{{ !isVariantGroupValueOrderable(item, optGroup.name, val) ? ' (' + $t('public.variantOptionNotAvailable') + ')' : '' }}
                      </option>
                    </select>
                  </div>
                </div>
                <p v-else-if="variantSkus(item).length && !variantOptionGroups(item).length" class="text-charcoal-blue/60 text-sm mt-2 font-medium uppercase tracking-wider pl-0 sm:pl-[5.5rem]">
                  {{ variantOptionGroupsSummary(item) }}
                </p>
              </div>
              <button
                v-else
                type="button"
                data-testid="public-menu-item"
                class="rms-menu-item-card group border-b border-concrete-gray pb-8 last:border-0 w-full text-left"
                :class="{ 'rms-item-unavailable': !isItemClickable(item, group) }"
                :aria-disabled="!isItemClickable(item, group)"
                @click="isItemClickable(item, group) ? onItemClick(item, group.category_name, $event) : showUnavailableToast(group, item, now)"
              >
                <div class="flex w-full items-center gap-4">
                  <img
                    v-if="itemCardDisplayImageUrl(item)"
                    :src="itemCardDisplayImageUrl(item)"
                    :alt="item.name || 'Untitled'"
                    loading="lazy"
                    class="w-20 h-20 md:w-24 md:h-24 shrink-0 aspect-square object-cover rounded"
                  />
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center justify-between gap-4 w-full mb-3">
                      <div class="flex flex-wrap items-center gap-2 min-w-0" :class="item.is_available === false ? 'text-charcoal-blue/60' : 'group-hover:[&_.heading-utilitarian]:text-[var(--t2-primary)]'">
                        <span class="heading-utilitarian text-3xl transition-colors" :style="itemUnavailableNow(item.availability) ? { opacity: 0.8 } : undefined">{{ item.name || 'Untitled' }}</span>
                      </div>
                      <span v-if="item.is_available !== false && itemPrice(item) != null" class="font-mono font-bold text-xl rms-t2-accent shrink-0" :style="itemUnavailableNow(item.availability) ? { opacity: 0.8 } : undefined">{{ formatPrice(itemPrice(item)) }}</span>
                      <span v-else-if="item.is_available === false" class="font-mono text-sm text-charcoal-blue/60 shrink-0">{{ $t('public.notAvailable') }}</span>
                    </div>
                    <p v-if="formatAvailabilityForDisplay(item.availability, now)" class="text-xs font-mono uppercase tracking-wider text-charcoal-blue/60 mt-0.5">{{ formatAvailabilityForDisplay(item.availability, now) }}</p>
                    <p v-if="item.description" class="text-sm text-charcoal-blue/60 font-medium uppercase tracking-wider">{{ item.description }}</p>
                    <span v-if="itemTags(item).length" class="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <span
                        v-for="tag in itemTags(item)"
                        :key="tag.uuid"
                        class="rms-public-tag inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-xs uppercase font-medium min-h-[28px] transition-[opacity,background-color] duration-200"
                        :style="tagStyle(tag)"
                        :title="tag.text"
                        role="img"
                        :aria-label="tag.text"
                        tabindex="0"
                      >
                        <span class="material-symbols-outlined text-sm shrink-0" style="color: var(--tag-fg)" aria-hidden="true">{{ tag.icon || 'label' }}</span>
                        <span v-if="tag.text" class="whitespace-nowrap" style="color: var(--tag-fg)">{{ tag.text }}</span>
                      </span>
                    </span>
                    <ul v-if="item.type === 'combo' && comboEntries(item).length" class="mt-2 list-none pl-4 md:pl-6 space-y-1 text-charcoal-blue/60 text-xs font-medium uppercase tracking-wider border-l-2 border-charcoal-blue/20 ml-0.5" aria-label="Combo contents">
                      <li v-for="(entry, eIdx) in comboEntries(item)" :key="eIdx">
                        {{ entry.name }} × {{ entry.quantity }}{{ entry.modifier_label ? ` (${entry.modifier_label})` : '' }}
                      </li>
                    </ul>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { usePublicUnavailableToast } from '@/composables/usePublicUnavailableToast'
import { usePublicMenuDisplay } from '@/composables/usePublicMenuDisplay'
import { usePublicMenuVariantSelect } from '@/composables/usePublicMenuVariantSelect'
import PublicMenuItemOwnerEditFab from '@/components/public/PublicMenuItemOwnerEditFab.vue'
import { formatAvailabilityForDisplay, isAvailableNow } from '@/utils/availability'
import { isVariantSkuPubliclyOrderable } from '@/utils/publicVariantSku'

const emit = defineEmits(['select-item'])
const { showUnavailableToast } = usePublicUnavailableToast()

const props = defineProps({
  menuGroups: { type: Array, default: () => [] },
  menuItems: { type: Array, default: () => [] },
  currency: { type: String, default: 'USD' },
  showCurrency: { type: Boolean, default: true },
  menuDescription: { type: String, default: '' },
  sectionTitle: { type: String, default: '' },
  /** Empty-state message (e.g. "No menu items", "No products", "No services") from parent based on business_type. */
  emptyMessage: { type: String, default: '' },
  showOwnerEditFab: { type: Boolean, default: false },
})

function onItemClick(item, categoryName, event) {
  emit('select-item', {
    item,
    categoryName: categoryName || 'Menu',
    sku: null,
    trigger: event?.currentTarget ?? null,
  })
}

const {
  displayGroups,
  formatPrice,
  itemPrice,
  itemTags,
  comboEntries,
  variantOptionGroupsSummary,
  variantOptionGroups,
  variantSkus,
  itemCardImageUrl,
} = usePublicMenuDisplay(props)

const {
  variantSelectId,
  resolvedVariantSku,
  variantSelectionValue,
  onVariantGroupChange,
  isVariantGroupValueOrderable,
} = usePublicMenuVariantSelect({
  displayGroups,
  variantSkus,
  variantOptionGroups,
})

function onVariantHeaderClick(item, categoryName, event) {
  const sku = resolvedVariantSku(item)
  emit('select-item', {
    item,
    categoryName: categoryName || 'Menu',
    sku: sku || null,
    trigger: event?.currentTarget ?? null,
  })
}

function itemCardDisplayImageUrl(item) {
  if (!item) return null
  if (item.type === 'with_variants') {
    const sku = resolvedVariantSku(item)
    return sku?.image_url ?? sku?.imageUrl ?? itemCardImageUrl(item)
  }
  return itemCardImageUrl(item)
}

const now = ref(new Date())
let nowInterval
onMounted(() => {
  nowInterval = setInterval(() => { now.value = new Date() }, 60 * 1000)
})
onUnmounted(() => {
  if (nowInterval) clearInterval(nowInterval)
})

function categoryUnavailableNow(availability) {
  return availability != null && typeof availability === 'object' && !isAvailableNow(availability, now.value)
}
function itemUnavailableNow(availability) {
  return availability != null && typeof availability === 'object' && !isAvailableNow(availability, now.value)
}

function isItemClickable(item, group) {
  if (!item || !group) return false
  if (categoryUnavailableNow(group.availability)) return false
  if (item.is_available === false) return false
  if (itemUnavailableNow(item.availability)) return false
  if (item.type === 'with_variants') {
    const sku = resolvedVariantSku(item)
    if (!sku || !isVariantSkuPubliclyOrderable(sku)) return false
  }
  return true
}

function tagStyle(tag) {
  const hex = tag?.color || '#64748b'
  return {
    '--tag-bg': `${hex}20`,
    '--tag-fg': hex,
  }
}
</script>

<style scoped>
.rms-public-tag {
  background-color: var(--tag-bg);
}
.rms-public-tag:hover {
  opacity: 0.9;
}
.rms-menu-item-card {
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  min-height: 44px;
  transition: opacity 0.15s;
}
.rms-menu-item-card:hover {
  opacity: 0.9;
}
.rms-menu-item-card--no-click {
  cursor: default;
}

.rms-menu-item-card {
  border: 1px dotted var(--t2-primary, #1152d4) !important;
  margin-bottom: 10px !important;
  padding: 10px 10px;
  border-radius: 10px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
}

.rms-t2-variant-header:focus-visible {
  outline: 2px solid var(--t2-primary, #1152d4);
  outline-offset: 2px;
}

.rms-t2-variant-select {
  appearance: auto;
}

.rms-category-unavailable.rms-category-items {
  filter: grayscale(0.85);
  backdrop-filter: blur(0.5px);
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
  cursor: not-allowed;
  transition: filter 0.2s, opacity 0.2s;
}

.rms-item-unavailable {
  filter: grayscale(0.7) blur(0.5px);
  opacity: 0.7;
  cursor: not-allowed;
}

.rms-menu-item-card:focus-visible {
  outline: 2px solid var(--t2-primary, #1152d4);
  outline-offset: 2px;
}
</style>
